/**
 * The few operations the player needs on a recording, without CodeMirror
 * (which the recruiter app doesn't carry): applying a recorded change to a
 * file's text, finding the lines two texts differ on, and SHA-256.
 */
import type { ChangeJSON } from './types';

/**
 * `content` with a recorded change applied. The change is CodeMirror's
 * ChangeSet.toJSON(): each number keeps that many characters, each array
 * deletes its first value's worth and inserts the rest joined by newlines.
 * Lengths count UTF-16 code units, newlines included, as CodeMirror's do.
 *
 * Throws when the change was made to a text of another length, as
 * CodeMirror's own apply does, so the player can say a part is missing.
 */
export function applyChange(content: string, changes: ChangeJSON): string {
  if (!Array.isArray(changes)) throw new RangeError('Not a change set');
  let at = 0;
  let out = '';
  for (const part of changes) {
    if (typeof part === 'number') {
      if (!Number.isInteger(part) || part < 0 || at + part > content.length) {
        throw new RangeError('Change does not fit the text');
      }
      out += content.slice(at, at + part);
      at += part;
    } else if (
      Array.isArray(part) &&
      typeof part[0] === 'number' &&
      Number.isInteger(part[0]) &&
      part[0] >= 0 &&
      part.slice(1).every((line) => typeof line === 'string')
    ) {
      const [deleted, ...inserted] = part;
      if (at + deleted > content.length) {
        throw new RangeError('Change does not fit the text');
      }
      out += inserted.join('\n');
      at += deleted;
    } else {
      throw new RangeError('Not a change set');
    }
  }
  if (at !== content.length) {
    throw new RangeError('Change does not fit the text');
  }
  return out;
}

/** The lines that differ between two texts: where they start (1-based) and how many there are on each side. */
export function changedLines(
  before: string,
  after: string,
): { from: number; removed: number; added: number } | null {
  if (before === after) return null;
  const a = before.split('\n');
  const b = after.split('\n');
  let start = 0;
  while (start < a.length && start < b.length && a[start] === b[start])
    start += 1;
  let endA = a.length;
  let endB = b.length;
  while (endA > start && endB > start && a[endA - 1] === b[endB - 1]) {
    endA -= 1;
    endB -= 1;
  }
  return { from: start + 1, removed: endA - start, added: endB - start };
}

export const hex = (bytes: ArrayBuffer | Uint8Array): string =>
  Array.from(new Uint8Array(bytes), (b) =>
    b.toString(16).padStart(2, '0'),
  ).join('');

/** Lowercase hex SHA-256, through WebCrypto. */
export async function sha256Hex(bytes: Uint8Array): Promise<string> {
  const digest = await crypto.subtle.digest('SHA-256', bytes as BufferSource);
  return hex(digest);
}
